import React, { useEffect, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError, API_BASE } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Activity, CheckCircle2, AlertCircle, Clock, RefreshCw, Loader2,
  FileVideo, Hash, BarChart3, Radio, Trash2
} from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle, AlertDialogTrigger,
} from "../components/ui/alert-dialog";

const STATUS_STYLE = {
  queued: "bg-blue-100 text-blue-800 border border-blue-200",
  processing: "bg-purple-100 text-purple-800 border border-purple-200",
  success: "bg-emerald-100 text-emerald-800 border border-emerald-200",
  failed: "bg-red-100 text-red-800 border border-red-200",
};

const KPI = [
  { key: "queued", label: "Queued", icon: Clock },
  { key: "processing", label: "Processing", icon: Activity },
  { key: "success", label: "Success", icon: CheckCircle2 },
  { key: "failed", label: "Failed", icon: AlertCircle },
];

function formatDuration(seconds) {
  if (!seconds || seconds <= 0) return "—";
  const s = Math.floor(seconds);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const r = s % 60;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m ${r}s`;
}

export default function DashboardPage() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [liveConnected, setLiveConnected] = useState(false);
  const wsRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const [s, v] = await Promise.all([
        api.get("/stats"),
        api.get("/videos?limit=20"),
      ]);
      setStats(s.data);
      setVideos(v.data.items);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  // WebSocket subscription for real-time updates
  useEffect(() => {
    const token = localStorage.getItem("garda_token") || "";
    const wsUrl = API_BASE.replace(/^http/, "ws") + "/ws/status" + (token ? `?token=${token}` : "");
    let ws;
    let closedByUs = false;
    let retryTimer;

    const connect = () => {
      ws = new WebSocket(wsUrl);
      wsRef.current = ws;
      ws.onopen = () => setLiveConnected(true);
      ws.onclose = () => {
        setLiveConnected(false);
        if (!closedByUs) retryTimer = setTimeout(connect, 4000);
      };
      ws.onerror = () => { /* will be followed by onclose */ };
      ws.onmessage = (ev) => {
        try {
          const msg = JSON.parse(ev.data);
          if (msg.type === "stats_dirty") {
            load();
            return;
          }
          if (msg.type === "video_deleted") {
            setVideos((curr) => curr.filter((v) => v.id !== msg.video_id));
            return;
          }
          if (msg.video) {
            setVideos((curr) => {
              const idx = curr.findIndex((v) => v.id === msg.video.id);
              if (idx === -1) return [msg.video, ...curr].slice(0, 20);
              const next = [...curr];
              next[idx] = { ...next[idx], ...msg.video };
              return next;
            });
          }
        } catch (e) {
          console.debug("ws message parse failed:", e, ev.data);
        }
      };
    };
    connect();

    return () => {
      closedByUs = true;
      if (retryTimer) clearTimeout(retryTimer);
      if (ws && ws.readyState === WebSocket.OPEN) {
        ws.close();
      } else if (ws) {
        // Wait for connection to open then close, or let onclose run naturally
        ws.onopen = () => ws.close();
      }
    };
  }, [load]);

  useEffect(() => {
    load();
    // Fallback polling every 15s in case WS drops
    const id = setInterval(load, 15000);
    return () => clearInterval(id);
  }, [load]);

  const retry = async (id) => {
    try {
      await api.post(`/videos/${id}/retry`);
      toast.success("Diantrekan ulang untuk diproses");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async (id, fileName) => {
    try {
      await api.delete(`/videos/${id}`);
      setVideos((curr) => curr.filter((v) => v.id !== id));
      toast.success(`File "${fileName}" dihapus beserta transkripnya`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="px-6 py-6 space-y-6" data-testid="dashboard-page">
      <header className="flex items-end justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Operasional</p>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900">Dashboard Monitoring</h1>
          <p className="text-sm text-slate-500 mt-1">Status pipeline transkripsi & ringkasan intelijen keyword.</p>
        </div>
        <div className="flex items-center gap-3">
          <span
            className={`inline-flex items-center gap-1.5 text-xs font-medium px-2 py-1 rounded-sm border ${liveConnected
                ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                : "border-slate-200 bg-slate-50 text-slate-600"
              }`}
            data-testid="ws-status-indicator"
          >
            <Radio className={`h-3 w-3 ${liveConnected ? "text-emerald-600 animate-pulse" : "text-slate-400"}`} />
            {liveConnected ? "LIVE" : "OFFLINE"}
          </span>
          <Button
            onClick={load}
            variant="outline"
            size="sm"
            className="rounded-sm border-slate-300"
            data-testid="dashboard-refresh-button"
          >
            <RefreshCw className="h-4 w-4 mr-2" /> Refresh
          </Button>
        </div>
      </header>

      {/* KPI */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {KPI.map(({ key, label, icon: Icon }) => (
          <div key={key} className="bg-white border border-slate-200 rounded-sm p-4" data-testid={`kpi-${key}`}>
            <div className="flex items-center justify-between">
              <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{label}</p>
              <Icon className="h-4 w-4 text-slate-400" />
            </div>
            <p className="mt-2 text-3xl font-bold tracking-tight font-mono">{stats?.videos?.[key] ?? 0}</p>
          </div>
        ))}
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        <div className="md:col-span-2 bg-white border border-slate-200 rounded-sm">
          <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
            <h2 className="text-sm font-semibold tracking-tight">Antrian & Hasil Proses</h2>
            <Link to="/upload" className="text-xs text-slate-600 hover:text-slate-900 underline" data-testid="dashboard-goto-upload">+ Tambah Rekaman</Link>
          </div>
          {loading ? (
            <div className="p-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
          ) : videos.length === 0 ? (
            <div className="p-10 text-center text-sm text-slate-500" data-testid="dashboard-empty">
              <FileVideo className="h-8 w-8 mx-auto text-slate-300 mb-2" />
              Belum ada rekaman. Mulai dengan ingest file AVI.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr className="text-left">
                    <th className="px-4 py-2 text-xs uppercase tracking-wider text-slate-500 font-semibold">File</th>
                    <th className="px-4 py-2 text-xs uppercase tracking-wider text-slate-500 font-semibold">Channel</th>
                    <th className="px-4 py-2 text-xs uppercase tracking-wider text-slate-500 font-semibold">Durasi</th>
                    <th className="px-4 py-2 text-xs uppercase tracking-wider text-slate-500 font-semibold">Status</th>
                    <th className="px-4 py-2 text-xs uppercase tracking-wider text-slate-500 font-semibold text-right">Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {videos.map((v) => (
                    <tr key={v.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`video-row-${v.id}`}>
                      <td className="px-4 py-2.5">
                        <p className="font-medium truncate max-w-[260px]" title={v.file_name}>{v.file_name}</p>
                        <p className="text-[11px] text-slate-500 font-mono">{v.id.slice(-10)} · {v.ingest_source}</p>
                      </td>
                      <td className="px-4 py-2.5 text-slate-700">{v.channel_name}</td>
                      <td className="px-4 py-2.5 font-mono text-slate-600">{formatDuration(v.duration)}</td>
                      <td className="px-4 py-2.5">
                        <span className={`inline-block px-2 py-0.5 text-[11px] font-medium rounded-sm ${STATUS_STYLE[v.processing_status] || ""}`}>
                          {v.processing_status}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-right space-x-2">
                        {v.processing_status === "success" && (
                          <Link
                            to={`/transcript/${v.id}`}
                            className="text-xs text-slate-700 hover:text-slate-900 underline"
                            data-testid={`view-transcript-${v.id}`}
                          >Lihat</Link>
                        )}
                        {v.processing_status === "failed" && (user.role === "admin" || user.role === "analis") && (
                          <Button size="sm" variant="outline" className="rounded-sm h-7 text-xs"
                            onClick={() => retry(v.id)}
                            data-testid={`retry-${v.id}`}
                          >
                            <RefreshCw className="h-3 w-3 mr-1" /> Retry
                          </Button>
                        )}
                        {user.role === "admin" && (
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <button
                                className="inline-flex h-7 w-7 items-center justify-center rounded-sm border border-slate-300 text-slate-400 hover:bg-red-50 hover:text-red-600 hover:border-red-300 transition-colors"
                                title="Hapus rekaman"
                                data-testid={`delete-${v.id}`}
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            </AlertDialogTrigger>
                            <AlertDialogContent className="rounded-sm" data-testid={`delete-dialog-${v.id}`}>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Hapus rekaman ini?</AlertDialogTitle>
                                <AlertDialogDescription className="text-sm text-slate-600">
                                  <span className="block font-medium text-slate-900 truncate">{v.file_name}</span>
                                  Tindakan ini akan menghapus file AVI/MP4 dari disk, audio hasil ekstraksi,
                                  transkrip, dan semua keyword hits secara permanen. <b>Tidak dapat dibatalkan.</b>
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel className="rounded-sm" data-testid={`delete-cancel-${v.id}`}>Batal</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => remove(v.id, v.file_name)}
                                  className="rounded-sm bg-red-600 hover:bg-red-700 text-white"
                                  data-testid={`delete-confirm-${v.id}`}
                                >
                                  Ya, Hapus
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="space-y-4">
          <div className="bg-white border border-slate-200 rounded-sm p-4">
            <h2 className="text-sm font-semibold tracking-tight flex items-center gap-2">
              <Hash className="h-4 w-4 text-slate-400" /> Top Keyword
            </h2>
            <div className="mt-3 space-y-1.5">
              {(stats?.top_keywords || []).map((k) => (
                <div key={k.keyword} className="flex items-center justify-between text-sm" data-testid={`top-kw-${k.keyword}`}>
                  <span className="truncate">{k.keyword}</span>
                  <span className="font-mono text-xs bg-slate-100 text-slate-700 px-2 py-0.5 rounded-sm">{k.count}</span>
                </div>
              ))}
              {(!stats?.top_keywords || stats.top_keywords.length === 0) && (
                <p className="text-xs text-slate-500">Belum ada hits keyword.</p>
              )}
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-sm p-4">
            <h2 className="text-sm font-semibold tracking-tight flex items-center gap-2">
              <BarChart3 className="h-4 w-4 text-slate-400" /> Kategori Aktif
            </h2>
            <div className="mt-3 flex flex-wrap gap-1.5">
              {(stats?.top_categories || []).map((c) => (
                <Badge key={c.category} variant="secondary" className="rounded-sm bg-slate-100 text-slate-800 hover:bg-slate-200 border-none font-normal">
                  {c.category} · <span className="font-mono ml-1">{c.count}</span>
                </Badge>
              ))}
              {(!stats?.top_categories || stats.top_categories.length === 0) && (
                <p className="text-xs text-slate-500">Belum ada hits kategori.</p>
              )}
            </div>
          </div>

          <div className="bg-slate-900 text-white border border-slate-900 rounded-sm p-4">
            <p className="text-[11px] uppercase tracking-wider text-slate-300">Repositori</p>
            <p className="text-2xl font-bold font-mono mt-1">{stats?.transcripts ?? 0}</p>
            <p className="text-xs text-slate-300 mt-1">transkrip tersimpan · {stats?.keyword_hits ?? 0} keyword hits</p>
          </div>
        </div>
      </div>
    </div>
  );
}
